import type { InteractionStep, OrchestratorAgent, SimpleAgentConfig } from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface AgentCommunicationFlowProps {
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  currentStep: InteractionStep | null;
  activeAgents: string[];
  primaryColor?: string;
}

interface CommunicationLine {
  from: string;
  to: string;
  active: boolean;
  message: string;
}

const AgentCommunicationFlow: React.FC<AgentCommunicationFlowProps> = ({
  orchestrator,
  agents,
  currentStep,
  activeAgents,
  primaryColor = '#3498db',
}) => {
  const [communicationLines, setCommunicationLines] = useState<CommunicationLine[]>([]);
  const [animatingLines, setAnimatingLines] = useState<Set<string>>(new Set());

  // Update communication lines based on current step
  useEffect(() => {
    if (!currentStep) {
      setCommunicationLines([]);
      return;
    }

    const newLines: CommunicationLine[] = [];

    if (currentStep.action === 'analyze' && currentStep.agent === 'orchestrator') {
      // Orchestrator is analyzing - show thinking
      newLines.push({
        from: 'orchestrator',
        to: 'orchestrator',
        active: true,
        message: 'Analyzing request and planning coordination...',
      });
    } else if (currentStep.action === 'call_agent') {
      // Orchestrator calling an agent
      newLines.push({
        from: 'orchestrator',
        to: currentStep.agent,
        active: true,
        message: `Delegating task: ${currentStep.description}`,
      });
    } else if (currentStep.action === 'use_tool') {
      // Agent using tools
      newLines.push({
        from: currentStep.agent,
        to: currentStep.agent,
        active: true,
        message: `Using specialized tools for analysis...`,
      });
    } else if (currentStep.action === 'synthesize') {
      // Orchestrator synthesizing responses
      activeAgents.forEach(agentId => {
        if (agentId !== 'orchestrator') {
          newLines.push({
            from: agentId,
            to: 'orchestrator',
            active: true,
            message: 'Sending analysis results...',
          });
        }
      });
    }

    setCommunicationLines(newLines);

    // Animate new lines
    const lineKeys = newLines.map(line => `${line.from}-${line.to}`);
    setAnimatingLines(new Set(lineKeys));

    // Remove animation after delay
    const timer = setTimeout(() => {
      setAnimatingLines(new Set());
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentStep, activeAgents]);

  const getAgentPosition = (agentId: string) => {
    if (agentId === 'orchestrator') {
      return { x: 50, y: 20 }; // Center top
    }
    
    const agentIndex = agents.findIndex(a => a.id === agentId);
    if (agentIndex === -1) return { x: 50, y: 50 };

    const totalAgents = agents.length;
    const angle = (agentIndex * 2 * Math.PI) / totalAgents;
    const radius = 35;
    const centerX = 50;
    const centerY = 60;

    return {
      x: centerX + radius * Math.cos(angle - Math.PI / 2),
      y: centerY + radius * Math.sin(angle - Math.PI / 2),
    };
  };

  const getAgentInfo = (agentId: string) => {
    if (agentId === 'orchestrator') {
      return { name: orchestrator.name, avatar: orchestrator.avatar };
    }
    const agent = agents.find(a => a.id === agentId);
    return { 
      name: agent?.name || 'Unknown', 
      avatar: agent?.avatar || '🤖' 
    };
  };

  const allAgentIds = ['orchestrator', ...agents.map(a => a.id)];

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      height: '400px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        fontSize: '1.2em',
        fontWeight: 'bold',
        marginBottom: '15px',
        color: '#2c3e50',
        textAlign: 'center',
      }}>
        🔄 Agent Communication Flow
      </div>

      {/* SVG for communication lines */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={primaryColor}
            />
          </marker>
          
          {/* Animated gradient for active lines */}
          <linearGradient id="activeLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.2">
              <animate
                attributeName="stop-opacity"
                values="0.2;0.8;0.2"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor={primaryColor} stopOpacity="0.8">
              <animate
                attributeName="stop-opacity"
                values="0.8;1;0.8"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.2">
              <animate
                attributeName="stop-opacity"
                values="0.2;0.8;0.2"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        </defs>

        {communicationLines.map((line, index) => {
          const fromPos = getAgentPosition(line.from);
          const toPos = getAgentPosition(line.to);
          const lineKey = `${line.from}-${line.to}`;
          const isAnimating = animatingLines.has(lineKey);
          const isSelfLoop = line.from === line.to;

          if (isSelfLoop) {
            // Self-loop for thinking/processing
            const centerX = (fromPos.x / 100) * 400; // Convert percentage to pixels
            const centerY = (fromPos.y / 100) * 400;
            const radius = 25;

            return (
              <g key={index}>
                <circle
                  cx={centerX}
                  cy={centerY - radius}
                  r={radius}
                  fill="none"
                  stroke={isAnimating ? 'url(#activeLineGradient)' : primaryColor}
                  strokeWidth="3"
                  strokeDasharray={isAnimating ? "5,5" : "none"}
                  opacity={line.active ? 1 : 0.3}
                >
                  {isAnimating && (
                    <animate
                      attributeName="stroke-dashoffset"
                      values="0;-10"
                      dur="0.5s"
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
                <polygon
                  points={`${centerX + radius - 5},${centerY - radius - 5} ${centerX + radius + 5},${centerY - radius} ${centerX + radius - 5},${centerY - radius + 5}`}
                  fill={primaryColor}
                  opacity={line.active ? 1 : 0.3}
                />
              </g>
            );
          }

          return (
            <g key={index}>
              <line
                x1={`${fromPos.x}%`}
                y1={`${fromPos.y}%`}
                x2={`${toPos.x}%`}
                y2={`${toPos.y}%`}
                stroke={isAnimating ? 'url(#activeLineGradient)' : primaryColor}
                strokeWidth="3"
                strokeDasharray={isAnimating ? "8,4" : "none"}
                markerEnd="url(#arrowhead)"
                opacity={line.active ? 1 : 0.3}
              >
                {isAnimating && (
                  <animate
                    attributeName="stroke-dashoffset"
                    values="0;-12"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                )}
              </line>
            </g>
          );
        })}
      </svg>

      {/* Agent nodes */}
      {allAgentIds.map(agentId => {
        const position = getAgentPosition(agentId);
        const agentInfo = getAgentInfo(agentId);
        const isActive = activeAgents.includes(agentId) || 
                        (currentStep?.agent === agentId);
        const isOrchestrator = agentId === 'orchestrator';

        return (
          <div
            key={agentId}
            style={{
              position: 'absolute',
              left: `${position.x}%`,
              top: `${position.y}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: isOrchestrator 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : isActive 
                ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor}aa)`
                : '#f8f9fa',
              border: isActive 
                ? `3px solid ${isOrchestrator ? '#667eea' : primaryColor}` 
                : '2px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5em',
              boxShadow: isActive 
                ? `0 4px 15px ${isOrchestrator ? 'rgba(102, 126, 234, 0.4)' : 'rgba(52, 152, 219, 0.4)'}` 
                : '0 2px 8px rgba(0,0,0,0.1)',
              animation: isActive ? 'pulse 2s infinite' : 'none',
              cursor: 'pointer',
            }}
            title={agentInfo.name}
          >
            {agentInfo.avatar}
          </div>
          
          {/* Agent label */}
          <div style={{
            position: 'absolute',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.7em',
            fontWeight: 'bold',
            color: isOrchestrator ? '#667eea' : '#2c3e50',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            maxWidth: '80px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {agentInfo.name.split(' ')[0]}
          </div>
        </div>
        );
      })}

      {/* Communication message display */}
      {communicationLines.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          right: '20px',
          background: 'rgba(52, 152, 219, 0.1)',
          border: `1px solid ${primaryColor}`,
          borderRadius: '8px',
          padding: '10px',
          fontSize: '0.85em',
          color: '#2c3e50',
          zIndex: 3,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            📡 Active Communication:
          </div>
          {communicationLines.map((line, index) => (
            <div key={index} style={{ marginBottom: '2px' }}>
              • {line.message}
            </div>
          ))}
        </div>
      )}

      {/* CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.05); }
          }
        `}
      </style>
    </div>
  );
};

export default AgentCommunicationFlow;